import { useEffect } from 'react';
import {
  useSavedLocationCreateApi,
  InferUseHookMutateApi,
  useSavedLocationUpdateApi,
  useSavedLocationApi,
} from '@hero/api-hooks';
import { getObjectId, Id } from '@hero/schema';
import {
  SavedLocationFormValues,
  useSavedLocationForm,
  UseSavedLocationFormOptions,
} from './useSavedLocationForm';

type SavedLocationCreateResult = InferUseHookMutateApi<typeof useSavedLocationCreateApi>['result'];
export interface UseSavedLocationCreateAdapterOptions {
  onLocationSaved?: (data: SavedLocationCreateResult) => void;
  /** Optional initial form values (e.g. pre-fill from context). Overwritten when loading an item via savedLocationId. */
  defaultValues?: UseSavedLocationFormOptions['defaultValues'];
  /** When set, load location for edit; when undefined, create mode. */
  savedLocationId?: Id;
  /** Lock the location type (e.g. when opening the modal from a client's CRM). */
  forceType?: UseSavedLocationFormOptions['forceType'];
  /** Force the client this location belongs to (used together with `forceType='client'`). */
  forceClientId?: UseSavedLocationFormOptions['forceClientId'];
}

export const useSavedLocationCreateAdapter = (options: UseSavedLocationCreateAdapterOptions) => {
  const createLocation = useSavedLocationCreateApi();
  const updateLocation = useSavedLocationUpdateApi();
  const savedLocationQuery = useSavedLocationApi(options.savedLocationId);
  const savedLocation = savedLocationQuery.data;

  async function onSubmit(data: SavedLocationFormValues) {
    try {
      let result: SavedLocationCreateResult;
      // When the form is launched in `forceType='client'` mode, the
      // caller's `forceClientId` is the source of truth; the form
      // values are double-checked here as a defense-in-depth measure.
      const clientId =
        options.forceType === 'client'
          ? options.forceClientId ?? data.clientId
          : options.forceType === 'own'
          ? undefined
          : data.clientId;
      // Form stores the id as a plain string; the API req type wants ObjectId.
      const payload = { ...data, clientId: clientId ? getObjectId(clientId) : undefined };
      if (savedLocation) {
        result = await updateLocation.mutateAsync({
          _id: getObjectId(savedLocation._id),
          ...payload,
        });
      } else {
        result = await createLocation.mutateAsync(payload);
      }
      options.onLocationSaved?.(result);
    } catch (error) {
      console.error('Failed to save location:', error);
    }
  }

  const formHook = useSavedLocationForm({
    onSubmit,
    defaultValues: options.defaultValues,
    forceType: options.forceType,
    forceClientId: options.forceClientId,
    // Edit mode (a `savedLocationId` was provided) auto-locks the
    // record's type + client so users can only change contact + map
    // pin + name. Create flows leave the toggles free.
    lockOnLoad: options.savedLocationId != null,
  });

  // When savedLocationId changes, reset form so we don't show stale data from a previous entity or create; then the effect below will set the form when the new entity loads (same pattern as contact/customer adapters).
  useEffect(() => {
    if (options.savedLocationId != null) {
      formHook.reset();
    }
  }, [options.savedLocationId, formHook.reset]);

  // When we have an id and data loaded, pass the entity to the form so it can set its values
  useEffect(() => {
    if (options.savedLocationId != null && savedLocation) {
      formHook.setSavedLocation(savedLocation);
    }
  }, [options.savedLocationId, savedLocation, formHook.setSavedLocation]);

  const reset = () => {
    createLocation.reset();
    updateLocation.reset();
    if (savedLocation) {
      formHook.setSavedLocation(savedLocation);
    } else {
      formHook.reset();
    }
  };

  const onSaveClick = () => {
    formHook.form.handleSubmit(formHook.onValidSubmit)();
  };

  return {
    savedLocation: savedLocationQuery,
    createLocation,
    updateLocation,
    formHook,
    reset,
    onSaveClick,
  };
};
